import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, X, Plus, Minus, Trash2 } from 'lucide-react';
import { shopApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const CartDropdown = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await shopApi.getCart();
      setCart(data);
    } catch (e) {}
  }, [isAuthenticated]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // Listen for custom cart-updated events
  useEffect(() => {
    const handler = () => fetchCart();
    window.addEventListener('cart-updated', handler);
    return () => window.removeEventListener('cart-updated', handler);
  }, [fetchCart]);

  const updateQty = async (index, delta) => {
    const item = cart.items[index];
    const newQty = item.quantity + delta;
    setLoading(true);
    try {
      await shopApi.updateCartItem(index, newQty);
      await fetchCart();
    } catch (e) {}
    setLoading(false);
  };

  const removeItem = async (index) => {
    setLoading(true);
    try {
      await shopApi.removeFromCart(index);
      await fetchCart();
    } catch (e) {}
    setLoading(false);
  };

  if (!isAuthenticated) return null;
  const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="relative" data-testid="cart-dropdown">
      <button
        onClick={() => { setOpen(!open); if (!open) fetchCart(); }}
        className="relative p-2 text-gray-400 hover:text-white transition-colors"
        data-testid="cart-icon-btn"
      >
        <ShoppingCart className="w-5 h-5" />
        {itemCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-[#8B5CF6] text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] px-1" data-testid="cart-badge">
            {itemCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 max-h-[80vh] bg-[#1a1035] border border-[#8B5CF6]/20 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="cart-panel">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-semibold text-sm">Shopping Cart ({itemCount})</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="overflow-y-auto max-h-[50vh] p-3 space-y-3">
              {cart.items.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-8">Your cart is empty</p>
              ) : (
                cart.items.map((item, idx) => (
                  <div key={idx} className="flex gap-3 p-2 rounded-lg bg-white/[0.03]" data-testid={`cart-item-${idx}`}>
                    {item.image && <img src={item.image} alt="" className="w-14 h-14 rounded-md object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.product_name}</p>
                      {Object.keys(item.selected_variants || {}).length > 0 && (
                        <p className="text-xs text-[#8B5CF6] mt-0.5">
                          {Object.entries(item.selected_variants).map(([k, v]) => `${k}: ${v}`).join(' / ')}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-0.5">{item.creator_name}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => updateQty(idx, -1)} disabled={loading || item.quantity <= 1} className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-30" data-testid={`cart-item-${idx}-minus`}>
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-xs font-medium w-5 text-center">{item.quantity}</span>
                          <button onClick={() => updateQty(idx, 1)} disabled={loading} className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-30" data-testid={`cart-item-${idx}-plus`}>
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">${item.line_total.toFixed(2)}</span>
                          <button onClick={() => removeItem(idx)} disabled={loading} className="text-red-400 hover:text-red-300" data-testid={`cart-item-${idx}-remove`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.items.length > 0 && (
              <div className="p-4 border-t border-white/10 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Total</span>
                  <span className="font-bold text-lg">${cart.total.toFixed(2)}</span>
                </div>
                <button
                  onClick={() => { setOpen(false); navigate('/checkout'); }}
                  className="w-full py-2.5 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="cart-checkout-btn"
                >
                  Checkout
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
